import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@medflex/components/stories/Icons';
import InputField from '@medflex/components/stories/InputField';
import ContactList from '@medflex/components/stories/ContactList';
import isSelf from '@medflex/utils/isSelf';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import isTeamMember from '@medflex/utils/isTeamMember';
import SearchHighlight from '@medflex/components/stories/SearchHighlight';
import useIsModal from '../../hooks/useIsModal';
import useUserProfile from '../../hooks/useUserProfile';
import getSubtitle from '../../../utils/getSubtitle';

// @todo move to component catalog when final

const ContactsSelect = ({ selectedContacts, contacts, onSelect }) => {
  const { isModal } = useIsModal();
  const isWideScreen = useIsWideScreen();
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('messenger');
  const ref = useRef();
  const { userProfile } = useUserProfile();

  const filteredContacts = useMemo(() => {
    if (contacts) {
      return contacts.reduce((acc, cur) => {
        if (!matchesSearchTerm(searchTerm, cur.user.fullNameWithTitle)) return acc;
        if (
          !cur.pairedAt &&
          !cur.user.organizationId &&
          !isTeamMember({
            contact: cur,
            physicianUserId: userProfile.attributes.physicianUserId,
          })
        ) {
          return acc;
        }
        if (
          cur.id &&
          !cur.pairedAt &&
          !isTeamMember({
            contact: cur,
            physicianUserId: userProfile.attributes.physicianUserId,
          })
        ) {
          return acc;
        }
        return [...acc, cur];
      }, []);
    }
    return [];
  }, [searchTerm, contacts]);

  useEffect(() => {
    if (ref && isModal && isWideScreen) {
      ref.current.focus();
    }
  }, [ref, isModal, isWideScreen]);

  const handleContactClick = ({ contact }) => {
    onSelect({ contact });
    setSearchTerm('');
  };

  return (
    <>
      <div className="mt-4">{t('ContactsSelect.participants.label', { allowHtml: true })}</div>
      <div className="flex mt-2 flex-shrink-0">
        <div className="bg-slate-50 flex-grow text-slate-800 italic px-4 py-3 mr-2 self-center text-sm lg:text-base">
          {selectedContacts.length > 0
            ? selectedContacts.map((c, index) => (
                <span key={c.id}>
                  {index !== 0 && ', '}
                  {isSelf(c.user) ? t('ContactsSelect.self_title') : c.user.fullNameWithTitle}
                </span>
              ))
            : t('ContactsSelect.participants.placeholder')}
        </div>
      </div>
      <div className="font-semibold mt-4">{t('ContactsSelect.search.label')}</div>
      <div className="flex mt-2 flex-grow-0 flex-shrink-0">
        <InputField
          placeholder={t('ContactsSelect.search.placeholder')}
          className="flex-shrink-0 flex-grow mr-2"
          onChange={event => {
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
          innerRef={ref}
          afterInput={
            <div className="pr-4 flex justiy-center items-center cursor-pointer">
              <IconSearch size="5" className="fill-current text-slate-500" />
            </div>
          }
        />
      </div>
      <div className="lg:overflow-auto flex-auto flex-shrink-0 flex flex-col lg:max-h-80 min-h-0 lg:min-h-60 mt-4">
        <div className="flex-auto min-h-0">
          {filteredContacts.length === 0 && (
            <div className="flex justify-center items-center h-64 text-slate-500">
              {t('ContactsSelect.no_results.text')}
            </div>
          )}
          <ContactList className="flex-auto min-h-0">
            {filteredContacts.map(contact => (
              <ContactList.Item
                key={contact.user.id}
                avatarSrc={isSelf(contact.user) ? null : contact.user.avatar}
                avatarInitials={isSelf(contact.user) ? null : contact.user.initials}
                avatarSize="8"
                showBorder={false}
                title={
                  isSelf(contact.user) ? (
                    t('address_book.self_title')
                  ) : (
                    <SearchHighlight
                      text={contact.user.fullNameWithTitle}
                      toHighlight={searchTerm}
                    />
                  )
                }
                selected={selectedContacts.map(({ user }) => user.id).includes(contact.user.id)}
                subtitle={getSubtitle(contact, userProfile, t)}
                enabled
                onClick={() => handleContactClick({ contact })}
                hasCheckBox
              />
            ))}
          </ContactList>
        </div>
      </div>
    </>
  );
};

ContactsSelect.propTypes = {
  selectedContacts: PropTypes.arrayOf(
    PropTypes.shape({
      pairedAt: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.string,
        avatar: PropTypes.string,
        initials: PropTypes.string,
        fullNameWithTitle: PropTypes.string,
        organizationId: PropTypes.string,
      }),
    }),
  ),
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      pairedAt: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.string,
        avatar: PropTypes.string,
        initials: PropTypes.string,
        fullNameWithTitle: PropTypes.string,
        organizationId: PropTypes.string,
      }),
    }),
  ),
  onSelect: PropTypes.func,
};

ContactsSelect.defaultProps = {
  selectedContacts: [],
  contacts: [],
  onSelect: () => {},
};

export default ContactsSelect;
